import { useEffect, useState } from "react";
import {
  AppBox,
  AppButton,
  AppModal,
  AppPaper,
  AppSkeletonGlobalCardList,
  AppText,
  AppTitle,
} from "../../../commons/components";
import AppBorderBox from "../../../commons/components/BorderBox";
import IconPersonAdd from "../../../commons/components/icons/person-add";
import { useAppDispatch, useAppSelector } from "../../../hooks/app";
import { groupArrayByProperty } from "../../../commons/utilities/array-utils";
import { pluralizeString, toSentenceCase } from "../../../commons/utilities/string-utils";
import { FootballTeamPlayer } from "../../../commons/models/football/football-team-player";
import TeamPlayerCard from "./components/teamPlayer/TeamPlayerCard";
import TeamPlayerEdit from "./components/teamPlayer/TeamPlayerEdit";
import {
  fetchManageTeamPlayerList,
  IManageTeamPlayersOption,
} from "../../../redux/slices/football/admin/team/squad/manage-team-players-slice";
import { useSnackbar } from "../../../redux/snackbarProvider";
import { IFootballTeamPlayer } from "../../../commons/models/football/interface/i-football-team-player";
import IconPersonDelete from "../../../commons/components/icons/person-delete";
import TeamPlayerRemove from "./components/teamPlayer/TeamPlayerRemove";
import { StatusEnum } from "../../../commons/enums/status-enum";
import TeamPlayerRemovalInprogress from "./components/teamPlayer/TeamPlayerRemoveInprogress";
import TeamPlayerRemoveDateAdd from "./components/teamPlayer/TeamPlayerRemoveDateAdd";
import IconCalendar from "../../../commons/components/icons/calendar";
import { teamPlayerRemove } from "../../../redux/slices/football/admin/team/squad/manage-team-player-remove-slice";
import { formatDateTime } from "../../../commons/utilities/date-utils";
import setDocumentTitle from "../../../commons/utilities/document-title";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../commons/components/interface";
import moment from "moment";
import IconEdit from "../../../commons/components/icons/edit";
import TeamMultiplePlayerAdd from "./components/teamPlayer/TeamMultiplePlayerAdd";
import { Can } from "../../../casl/Can";
import { RolePolicyActionEnum } from "../../../commons/enums/role-policy-action.enum";

export default function TeamManageSquad() {
  const { teamInfo } = useAppSelector((state) => state.footballManageTeam);
  const [isPlayerEdit, setPlayerEdit] = useState(false);
  const [isMultiplePlayerAdd, setMultiplePlayerAdd] = useState(false);
  const [groupedPlayers, setGroupedPlayers] = useState<
    {
      [x: string]: string | FootballTeamPlayer[];
      items: FootballTeamPlayer[];
    }[]
  >([]);
  const [sortedGroupedPlayers, setSortedGroupedPlayers] = useState<
    {
      [x: string]: string | FootballTeamPlayer[];
      items: FootballTeamPlayer[];
    }[]
  >([]);
  const [selectedTeamPlayer, setSelectedTeamPlayer] = useState<IFootballTeamPlayer | null>(null);
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { manageTeamPlayersList, manageTeamPlayersListStatus } = useAppSelector(
    (state) => state.footballManageTeamPlayers
  );
  const { manageTeamPlayerRemoveStatus: teamPlayerRemoveStatus } = useAppSelector(
    (state) => state.footballManageTeamPlayerRemove
  );
  const { manageTeamMultiplePlayersAddStatus, manageTeamPlayerEditStatus } = useAppSelector(
    (state) => state.footballManageTeamPlayerAddEdit
  );
  const [isRemoveTeamPlayer, setRemoveTeamPlayer] = useState(false);
  const [isRemovalConfirm, setRemovalConfirm] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState<FootballTeamPlayer[]>();
  const [minDate, setMinDate] = useState<Date>();

  useEffect(() => {
    if (teamInfo.id) {
      const playerListOption: IManageTeamPlayersOption = {
        teamId: teamInfo.id,
      };
      dispatch(fetchManageTeamPlayerList(playerListOption));
    }
  }, [teamInfo]);

  useEffect(() => {
    if (manageTeamPlayersList.length > 0) {
      setTeamPlayers(manageTeamPlayersList.all());
      const formattedPlayers: FootballTeamPlayer[] = manageTeamPlayersList.mapArray((player) => ({
        ...player,
        position: player.position,
      }));
      setGroupedPlayers(groupArrayByProperty(formattedPlayers, "position"));
    } else {
      setGroupedPlayers([]);
    }
  }, [manageTeamPlayersList]);

  useEffect(() => {
    if (groupedPlayers.length > 0) {
      const customOrder = ["GOALKEEPER", "DEFENDER", "MIDFIELDER", "FORWARD"];
      const sortedData = groupedPlayers.sort((a, b) => {
        return customOrder.indexOf(a.position as string) - customOrder.indexOf(b.position as string);
      });
      setSortedGroupedPlayers(sortedData);
    } else {
      setSortedGroupedPlayers([]);
    }
  }, [groupedPlayers]);

  const handlePlayerRemoveConfirm = (date: Date) => {
    const playerRemovalInfo = {
      endAt: formatDateTime(date, "YYYY-MM-DD HH:mm:00"),
    };
    if (selectedTeamPlayer) {
      setRemovalConfirm(false);
      dispatch(
        teamPlayerRemove({
          teamId: teamInfo.id,
          playerId: selectedTeamPlayer.player.id,
          playerInfo: playerRemovalInfo,
        })
      )
        .unwrap()
        .then(() => {
          setSelectedTeamPlayer(null);
          addSnackbar({
            key: "player-remove",
            text: "Player Removed Successfully",
            variant: "success",
          });
          const playerListOption: IManageTeamPlayersOption = {
            teamId: teamInfo.id,
          };
          dispatch(fetchManageTeamPlayerList(playerListOption));
          setSelectedTeamPlayer(null);
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
  };

  useEffect(() => {
    if (selectedTeamPlayer) {
      const playerStartAt = moment(selectedTeamPlayer.startAt).toDate();
      setMinDate(playerStartAt);
    }
  }, [selectedTeamPlayer]);

  return (
    <>
      <AppBox mb="sm" gap="sm" flexDirection="column">
        <AppBox alignItems="center" justifyContent="space-between">
          <AppTitle as="h5">Squad</AppTitle>
          <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
            {manageTeamPlayersList.length > 0 && (
              <AppButton label="Add Player" variant="light" onClick={() => setMultiplePlayerAdd(true)} />
            )}
          </Can>
        </AppBox>
        {manageTeamPlayersList.length === 0 ? (
          <AppPaper padding="sm">
            <AppBorderBox borderVariant="dashed">
              <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center" p="sm">
                <AppBox alignItems="center" flexDirection="column" justifyContent="center" gap="xs">
                  <IconPersonAdd width={40} height={40} color="rgba(var(--border-200))" />
                  <AppText fontWeight="semibold">
                    There are not any players in the team.
                    <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                      To add players, click 'Add Player' and select from the list.
                    </Can>
                  </AppText>
                </AppBox>
                <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                  <AppButton label="Add Player" onClick={() => setMultiplePlayerAdd(true)} />
                </Can>
              </AppBox>
            </AppBorderBox>
          </AppPaper>
        ) : (
          <>
            {manageTeamPlayersListStatus === StatusEnum.Loading ? (
              <>
                <AppSkeletonGlobalCardList numberOfItems={3} withAvatar />
                <AppSkeletonGlobalCardList numberOfItems={6} withAvatar />
                <AppSkeletonGlobalCardList numberOfItems={5} withAvatar />
                <AppSkeletonGlobalCardList numberOfItems={5} withAvatar />
              </>
            ) : (
              <>
                {sortedGroupedPlayers.map((groupedPlayer, index) => (
                  <AppBox key={index} flexDirection="column" gap="xs">
                    <AppTitle as="h5">{toSentenceCase(pluralizeString(groupedPlayer.position as string))}</AppTitle>
                    <AppBox flexDirection="column" gap="sm">
                      {(groupedPlayer.items as FootballTeamPlayer[]).map((player) => (
                        <TeamPlayerCard
                          key={player.id}
                          playerInfo={player}
                          onPlayerRemove={(player) => {
                            setSelectedTeamPlayer(player);
                            setRemoveTeamPlayer(true);
                          }}
                          onPlayerEdit={(player) => {
                            setSelectedTeamPlayer(player);
                            setPlayerEdit(true);
                          }}
                        />
                      ))}
                    </AppBox>
                  </AppBox>
                ))}
              </>
            )}
          </>
        )}
      </AppBox>
      <AppModal
        opened={isPlayerEdit}
        onClose={() => {
          setSelectedTeamPlayer(null);
          setPlayerEdit(false);
          setDocumentTitle([`${teamInfo.shortName}`, "Manage"]);
        }}
        titleIcon={selectedTeamPlayer ? <IconEdit /> : <IconPersonAdd />}
        title={selectedTeamPlayer ? "Edit Team Player" : "Add Player To Team"}
        withLoadingButton={manageTeamPlayerEditStatus === StatusEnum.Loading}
        withoutButtons
      >
        <TeamPlayerEdit
          team={teamInfo}
          existingTeamPlayers={teamPlayers}
          selectedTeamPlayer={selectedTeamPlayer}
          onPlayerAddEdit={() => {
            setSelectedTeamPlayer(null);
            setPlayerEdit(false);
            setDocumentTitle([`${teamInfo.shortName}`, "Manage"]);
          }}
        />
      </AppModal>
      <AppModal
        modalSize="xl"
        opened={isMultiplePlayerAdd}
        onClose={() => {
          setMultiplePlayerAdd(false);
          setDocumentTitle([`${teamInfo.shortName}`, "Manage"]);
        }}
        titleIcon={<IconPersonAdd />}
        title={"Add Player To Team"}
        withLoadingButton={manageTeamMultiplePlayersAddStatus === StatusEnum.Loading}
        withoutButtons
      >
        <TeamMultiplePlayerAdd
          team={teamInfo}
          onAdd={() => {
            setMultiplePlayerAdd(false);
            setDocumentTitle([`${teamInfo.shortName}`, "Manage"]);
          }}
          onCancel={() => {
            setMultiplePlayerAdd(false);
            setDocumentTitle([`${teamInfo.shortName}`, "Manage"]);
          }}
        />
      </AppModal>
      <AppModal
        opened={isRemoveTeamPlayer}
        onClose={() => {
          setRemoveTeamPlayer(false);
          setSelectedTeamPlayer(null);
        }}
        title="Want To Remove This Player?"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        confirmButtonLabel="Remove"
        onSave={() => {
          setRemoveTeamPlayer(false);
          setRemovalConfirm(true);
        }}
      >
        <TeamPlayerRemove teamPlayer={selectedTeamPlayer as IFootballTeamPlayer} />
      </AppModal>
      <AppModal
        opened={isRemovalConfirm}
        onClose={(e) => {
          setRemovalConfirm(false);
          setSelectedTeamPlayer(null);
        }}
        title="Add Player's Release Date"
        titleIcon={<IconCalendar />}
        withoutButtons
      >
        <TeamPlayerRemoveDateAdd
          minDate={minDate}
          onAdd={(date) => {
            handlePlayerRemoveConfirm(date);
          }}
          onCancel={() => {
            setRemovalConfirm(false);
            setSelectedTeamPlayer(null);
          }}
        />
      </AppModal>
      <AppModal
        opened={teamPlayerRemoveStatus === StatusEnum.Loading}
        title="Removing Team"
        titleIcon={<IconPersonDelete />}
        iconColor="danger"
        withoutCancelButton
        withoutButtons
        withoutCloseButton
      >
        <TeamPlayerRemovalInprogress teamPlayer={selectedTeamPlayer as IFootballTeamPlayer} />
      </AppModal>
    </>
  );
}
